import { detectCountryFromIP } from './geo';

export const DEFAULT_CURRENCY = 'USD';
export const CURRENCY_COOKIE_NAME = 'invoicepoint_currency';

/**
 * Top world currencies offered in the selector.
 * `symbol` is used as a fallback when Intl formatting is unavailable.
 */
export const SUPPORTED_CURRENCIES = [
    { code: 'USD', symbol: '$', label: 'US Dollar' },
    { code: 'EUR', symbol: '€', label: 'Euro' },
    { code: 'GBP', symbol: '£', label: 'British Pound' },
    { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
    { code: 'JPY', symbol: '¥', label: 'Japanese Yen' },
    { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
    { code: 'BRL', symbol: 'R$', label: 'Brazilian Real' },
    { code: 'MXN', symbol: 'MX$', label: 'Mexican Peso' },
    { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc' },
];

const CURRENCY_BY_CODE = SUPPORTED_CURRENCIES.reduce((map, currency) => {
    map[currency.code] = currency;
    return map;
}, {});

// Map ISO 3166-1 alpha-2 country codes to a default currency.
export const COUNTRY_TO_CURRENCY = {
    // US Dollar
    US: 'USD', PR: 'USD', EC: 'USD', SV: 'USD', PA: 'USD', TL: 'USD',
    ZW: 'USD', GU: 'USD', VI: 'USD', AS: 'USD', MP: 'USD',
    // Euro
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR',
    AT: 'EUR', IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR',
    SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', MT: 'EUR',
    CY: 'EUR', MC: 'EUR', SM: 'EUR', VA: 'EUR', AD: 'EUR', ME: 'EUR',
    XK: 'EUR',
    // British Pound
    GB: 'GBP', IM: 'GBP', JE: 'GBP', GG: 'GBP',
    // Canadian Dollar
    CA: 'CAD',
    // Australian Dollar
    AU: 'AUD', KI: 'AUD', NR: 'AUD', TV: 'AUD',
    // Japanese Yen
    JP: 'JPY',
    // Indian Rupee
    IN: 'INR',
    // Brazilian Real
    BR: 'BRL',
    // Mexican Peso
    MX: 'MXN',
    // Swiss Franc
    CH: 'CHF', LI: 'CHF',
};

export function isSupportedCurrency(code) {
    return Boolean(code) && Object.prototype.hasOwnProperty.call(CURRENCY_BY_CODE, code.toUpperCase());
}

export function normalizeCurrency(code) {
    if (!code) {
        return DEFAULT_CURRENCY;
    }

    const upperCode = String(code).toUpperCase();
    return isSupportedCurrency(upperCode) ? upperCode : DEFAULT_CURRENCY;
}

export function getCurrencyMeta(code) {
    return CURRENCY_BY_CODE[normalizeCurrency(code)];
}

export function getCurrencyOptions() {
    return SUPPORTED_CURRENCIES.map((currency) => ({
        value: currency.code,
        label: `${currency.label} (${currency.code})`,
    }));
}

function readCurrencyCookie() {
    if (typeof document === 'undefined') {
        return null;
    }

    const cookieParts = document.cookie.split(';');
    for (const cookiePart of cookieParts) {
        const [rawKey, rawValue] = cookiePart.trim().split('=');
        if (rawKey === CURRENCY_COOKIE_NAME) {
            return normalizeCurrency(decodeURIComponent(rawValue || ''));
        }
    }

    return null;
}

export { readCurrencyCookie };

export function persistCurrency(code) {
    const normalizedCurrency = normalizeCurrency(code);

    if (typeof document !== 'undefined') {
        document.cookie = `${CURRENCY_COOKIE_NAME}=${encodeURIComponent(normalizedCurrency)}; path=/; max-age=31536000; SameSite=Lax`;
    }

    return normalizedCurrency;
}

/**
 * Synchronous preference: saved cookie, otherwise the default currency.
 */
export function getPreferredCurrency() {
    return readCurrencyCookie() || DEFAULT_CURRENCY;
}

/**
 * Detect a currency from the visitor's IP address.
 * Returns a supported currency code, or null when detection fails.
 */
export async function detectCurrencyFromIP() {
    const countryCode = await detectCountryFromIP();
    if (!countryCode) {
        return null;
    }

    const mapped = COUNTRY_TO_CURRENCY[countryCode];
    return mapped && isSupportedCurrency(mapped) ? mapped : null;
}

/**
 * Async preference: cookie → IP geolocation → default.
 */
export async function getPreferredCurrencyAsync() {
    const cookieCurrency = readCurrencyCookie();
    if (cookieCurrency) {
        return cookieCurrency;
    }

    const geoCurrency = await detectCurrencyFromIP();
    if (geoCurrency) {
        return geoCurrency;
    }

    return DEFAULT_CURRENCY;
}

/**
 * Parse a numeric amount from either a number or a localized currency string
 * such as "$1,250.00", "1.250,00 €" or "€2.400,00".
 */
export function parseAmount(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (value === null || value === undefined) {
        return 0;
    }

    let str = String(value).replace(/[^\d.,-]/g, '').trim();
    if (!str) {
        return 0;
    }

    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    let decimalSeparator = null;

    if (lastComma > -1 && lastDot > -1) {
        decimalSeparator = lastComma > lastDot ? ',' : '.';
    } else if (lastComma > -1) {
        decimalSeparator = /,\d{1,2}$/.test(str) ? ',' : null;
    } else if (lastDot > -1) {
        decimalSeparator = /\.\d{1,2}$/.test(str) ? '.' : null;
    }

    if (decimalSeparator) {
        const thousandSeparator = decimalSeparator === ',' ? '.' : ',';
        str = str.split(thousandSeparator).join('');
        str = str.replace(decimalSeparator, '.');
    } else {
        str = str.replace(/[.,]/g, '');
    }

    const parsed = parseFloat(str);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Format an amount using the selected currency and the active locale.
 * Accepts numbers or localized currency strings.
 */
export function formatCurrencyAmount(amount, currencyCode, locale) {
    const value = parseAmount(amount);
    const meta = getCurrencyMeta(currencyCode);

    try {
        return new Intl.NumberFormat(locale || undefined, {
            style: 'currency',
            currency: meta.code,
        }).format(value);
    } catch {
        return `${meta.symbol}${value.toFixed(2)}`;
    }
}
